import type { Observable } from 'rxjs';
import {
  filter,
  finalize,
  from,
  fromEvent,
  map,
  merge,
  mergeMap,
  of,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  throttleTime,
} from 'rxjs';
import type { DeviceFrameRatio, RemoteFrameSize } from '../../models/screenShare.model';

export abstract class ScreenShareUtil {
  static computeContainedSize(
    containerEl: HTMLElement,
    { deviceFrameHeight, deviceFrameWidth }: RemoteFrameSize,
  ): { width: number; height: number } {
    const childRatio = deviceFrameWidth / deviceFrameHeight;
    const containerHeight = containerEl.clientHeight;
    const containerWidth = containerEl.clientWidth;
    const containerRatio = containerWidth / containerHeight;

    if (containerRatio > childRatio) {
      // Container is wider: fit by height.
      const height = containerHeight;
      const width = height * childRatio;
      return { width, height };
    } else {
      // Container is narrower: fit by width.
      const width = containerWidth;
      const height = width / childRatio;
      return { width, height };
    }
  }

  static finallyForRTCPeer(peerConnection: RTCPeerConnection, dataChannel: RTCDataChannel): void {
    peerConnection.getSenders().forEach(({ track }) => {
      if (track && track.readyState !== 'ended') {
        track.stop();
      }
    });
    dataChannel.close();
    peerConnection.close();
  }

  static initiateCanvasAndVideo(
    remoteStream: MediaStream,
    containerEl: HTMLElement,
  ): { video: HTMLVideoElement; canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D | null } {
    const video = document.createElement('video');
    video.srcObject = remoteStream;
    video.playsInline = true;
    video.muted = true;
    video.autoplay = true;
    video.style.display = 'none';

    // 2. Create canvas
    const canvas = document.createElement('canvas');
    canvas.style.width = '100%';
    canvas.style.height = 'auto';

    containerEl.appendChild(video);
    containerEl.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    return { video, canvas, ctx };
  }

  static listenGesture(
    canvas: HTMLCanvasElement,
    dataChannel: RTCDataChannel,
    deviceFrameRatio: DeviceFrameRatio,
  ): Observable<MouseEvent> {
    return fromEvent<MouseEvent>(canvas, 'mousedown').pipe(
      tap((mouseEvent) => ScreenShareUtil.startGesture(dataChannel, mouseEvent, deviceFrameRatio)),
      switchMap(() =>
        merge(
          ScreenShareUtil.listenFinishGesture(canvas, dataChannel, deviceFrameRatio),
          ScreenShareUtil.listenMoveGesture(canvas, dataChannel, deviceFrameRatio),
        ),
      ),
    );
  }

  static onGestureFinish(canvas: HTMLCanvasElement): Observable<MouseEvent> {
    return merge(fromEvent<MouseEvent>(canvas, 'mouseout'), fromEvent<MouseEvent>(canvas, 'mouseup')).pipe(take(1));
  }

  static onIceCandidateUnSafe(peerConnection: RTCPeerConnection): Observable<RTCPeerConnectionIceEvent> {
    return fromEvent<RTCPeerConnectionIceEvent>(peerConnection, 'icecandidate');
  }

  static onRtcConnectionProblemSafe(peerConnection: RTCPeerConnection): Observable<void> {
    return merge(
      fromEvent(peerConnection, 'connectionstatechange'),
      fromEvent(peerConnection, 'iceconnectionstatechange'),
    ).pipe(
      startWith(void 0),
      map(() => [peerConnection.connectionState, peerConnection.iceConnectionState]),
      filter((states) => states.includes('failed')),
      map(() => void 0),
    );
  }

  static onRtcDataChannelProblemSafe(dataChannel: RTCDataChannel): Observable<void> {
    return merge(fromEvent(dataChannel, 'close')).pipe(
      startWith(void 0),
      map(() => dataChannel.readyState),
      filter((readyState) => readyState === 'closed'),
      take(1),
      map(() => void 0),
    );
  }

  static onRtcTrackProblemSafe(peerConnection: RTCPeerConnection): Observable<void> {
    return ScreenShareUtil.onTrackSafe(peerConnection).pipe(
      mergeMap((track) => ScreenShareUtil.onTrackEndedSafe(track)),
      take(1),
      map(() => void 0),
    );
  }

  static onSessionAndConnectivityConnectedSafe(peerConnection: RTCPeerConnection): Observable<void> {
    return merge(
      fromEvent(peerConnection, 'connectionstatechange'),
      fromEvent(peerConnection, 'iceconnectionstatechange'),
    ).pipe(
      startWith(void 0),
      map(() => [peerConnection.connectionState, peerConnection.iceConnectionState]),
      filter((states) => states.every((state) => state === 'connected')),
      take(1),
      map(() => void 0),
    );
  }

  static onTrackEndedSafe(track: MediaStreamTrack & { ended: boolean }): Observable<void> {
    return track.ended
      ? of(void 0)
      : fromEvent(track, 'ended').pipe(
          take(1),
          map(() => void 0),
        );
  }

  static onTrackSafe(peerConnection: RTCPeerConnection): Observable<MediaStreamTrack & { ended: boolean }> {
    return merge(from(peerConnection.getReceivers()), fromEvent<RTCTrackEvent>(peerConnection, 'track')).pipe(
      map(({ track }) => track),
    ) as Observable<MediaStreamTrack & { ended: boolean }>;
  }

  static onVideoHaveMetadataSafe(video: HTMLVideoElement): Observable<void> {
    return video.readyState >= HTMLMediaElement.HAVE_METADATA
      ? of(void 0)
      : fromEvent(video, 'loadedmetadata').pipe(
          take(1),
          map(() => void 0),
        );
  }

  static onWindowProblemUnsafe(): Observable<void> {
    window.addEventListener('beforeunload', ScreenShareUtil.triggerConfirmationPopupOnLeave, { capture: true });
    return merge(fromEvent(window, 'beforeunload')).pipe(
      map(() => void 0),
      take(1),
      finalize(() =>
        window.removeEventListener('beforeunload', ScreenShareUtil.triggerConfirmationPopupOnLeave, { capture: true }),
      ),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static parseJson(data: string): Record<string, any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parsedData = JSON.parse(data) as Record<string, any>;
    if (parsedData && typeof parsedData === 'object') {
      return parsedData;
    } else {
      throw new Error(`JSON not correct ${data}`);
    }
  }

  static sendMessage(dataChannel: RTCDataChannel, message: string): void {
    if (dataChannel.readyState === 'open') {
      dataChannel.send(message);
    }
  }

  static transformAnswerToString(answer: RTCSessionDescriptionInit): string {
    return JSON.stringify(answer);
  }

  private static continueGesture(
    dataChannel: RTCDataChannel,
    mouseEvent: MouseEvent,
    deviceFrameRatio: DeviceFrameRatio,
  ): void {
    ScreenShareUtil.sendGestureMessage(dataChannel, 'C', mouseEvent, deviceFrameRatio);
  }

  private static finishGesture(
    dataChannel: RTCDataChannel,
    mouseEvent: MouseEvent,
    deviceFrameRatio: DeviceFrameRatio,
  ): void {
    ScreenShareUtil.sendGestureMessage(dataChannel, 'F', mouseEvent, deviceFrameRatio);
  }

  private static listenFinishGesture(
    canvas: HTMLCanvasElement,
    dataChannel: RTCDataChannel,
    deviceFrameRatio: DeviceFrameRatio,
  ): Observable<MouseEvent> {
    return ScreenShareUtil.onGestureFinish(canvas).pipe(
      tap((mouseEvent) => ScreenShareUtil.finishGesture(dataChannel, mouseEvent, deviceFrameRatio)),
    );
  }

  private static listenMoveGesture(
    canvas: HTMLCanvasElement,
    dataChannel: RTCDataChannel,
    deviceFrameRatio: DeviceFrameRatio,
  ): Observable<MouseEvent> {
    return fromEvent<MouseEvent>(canvas, 'mousemove').pipe(
      throttleTime(10),
      tap((mouseEvent) => ScreenShareUtil.continueGesture(dataChannel, mouseEvent, deviceFrameRatio)),
      takeUntil(ScreenShareUtil.onGestureFinish(canvas)),
    );
  }

  private static sendGestureMessage(
    dataChannel: RTCDataChannel,
    type: 'C' | 'F' | 'S',
    { offsetX, offsetY }: MouseEvent,
    { horizontalRatio, verticalRatio }: DeviceFrameRatio,
  ): void {
    const remoteX = Math.round(offsetX * horizontalRatio);
    const remoteY = Math.round(offsetY * verticalRatio);
    const gestureMessage = `${type}-${remoteX}-${remoteY}`;
    ScreenShareUtil.sendMessage(dataChannel, gestureMessage);
  }

  private static startGesture(
    dataChannel: RTCDataChannel,
    mouseEvent: MouseEvent,
    deviceFrameRatio: DeviceFrameRatio,
  ): void {
    ScreenShareUtil.sendGestureMessage(dataChannel, 'S', mouseEvent, deviceFrameRatio);
  }

  private static triggerConfirmationPopupOnLeave(event: BeforeUnloadEvent): void {
    event.preventDefault();
    event.returnValue = 'nop';
  }
}
